<template>
  <div class="all-projects-container">
    <!-- Header Section -->
    <header class="projects-header">
      <div class="container">
        <nav class="breadcrumb">
          <a href="/" class="breadcrumb-link">{{ locale === 'tr' ? 'Ana Sayfa' : 'Home' }}</a>
          <span class="breadcrumb-separator">›</span>
          <span class="breadcrumb-current">{{ locale === 'tr' ? 'Tüm Projelerimiz' : 'All Projects' }}</span>
        </nav>
        <h1 class="page-title">{{ locale === 'tr' ? 'Tüm Projelerimiz' : 'All Our Projects' }}</h1>
        <p class="page-description">
          {{ locale === 'tr' 
            ? 'Yenilenebilir enerji alanında gerçekleştirdiğimiz tüm projelerimizi keşfedin.' 
            : 'Discover all our projects in the field of renewable energy.'
          }}
        </p>
      </div>
    </header>

    <!-- Projects Grid Section -->
    <section class="projects-content">
      <div class="container">
        <!-- Loading State -->
        <div v-if="isLoading" class="loading-state">
          <div class="loading-spinner"></div>
          <p>{{ locale === 'tr' ? 'Projeler yükleniyor...' : 'Loading projects...' }}</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-state">
          <div class="error-icon">⚠️</div>
          <h3>{{ locale === 'tr' ? 'Hata Oluştu' : 'Error Occurred' }}</h3>
          <p>{{ error }}</p>
          <button @click="loadProjects" class="retry-btn">
            {{ locale === 'tr' ? 'Tekrar Dene' : 'Retry' }}
          </button>
        </div>

        <!-- Projects Grid -->
        <div v-else class="projects-wrapper">
          <div class="projects-info">
            <div class="results-count">
              {{ locale === 'tr' 
                ? `Toplam ${totalProjects} projeden ${projects.length} tanesi gösteriliyor` 
                : `Showing ${projects.length} of ${totalProjects} total projects`
              }}
            </div>
            <div class="page-info">
              {{ locale === 'tr' ? 'Sayfa' : 'Page' }} {{ currentPage }} / {{ totalPages }}
            </div>
          </div>

          <div class="projects-grid">
            <div 
              v-for="project in projects" 
              :key="project.id" 
              class="project-card"
              @click="goToProjectDetail(project.id)"
            >
              <div class="project-image-container">
                <img 
                  :src="getProjectImage(project)" 
                  :alt="project.title"
                  class="project-image"
                  @error="handleImageError"
                  loading="lazy"
                />
                <div class="project-overlay">
                  <button class="view-details-btn">
                    {{ locale === 'tr' ? 'Detayları Gör' : 'View Details' }}
                  </button>
                </div>
              </div>
              
              <div class="project-content">
                <h3 class="project-title">{{ project.title }}</h3>
                <p class="project-description">{{ project.description }}</p>
                
                <div class="project-meta">
                  <div class="meta-item" v-if="project.capacity">
                    <i class="fas fa-bolt"></i>
                    <span>{{ project.capacity }}</span>
                  </div>
                  <div class="meta-item" v-if="project.location">
                    <i class="fas fa-map-marker-alt"></i>
                    <span>{{ project.location }}</span>
                  </div>
                  <div class="meta-item" v-if="project.completion_date">
                    <i class="fas fa-calendar-alt"></i>
                    <span>{{ formatDate(project.completion_date) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Pagination -->
          <div class="pagination-wrapper" v-if="totalPages > 1">
            <div class="pagination">
              <!-- Previous Button -->
              <button 
                class="pagination-btn prev-btn" 
                :disabled="currentPage === 1"
                @click="goToPrevPage"
              >
                <i class="fas fa-chevron-left"></i>
                {{ locale === 'tr' ? 'Önceki' : 'Previous' }}
              </button>

              <!-- Page Numbers -->
              <div class="page-numbers">
                <button 
                  v-for="page in visiblePages" 
                  :key="page"
                  :class="['page-number', { active: page === currentPage }]"
                  @click="goToPage(page)"
                >
                  {{ page }}
                </button>
              </div>

              <!-- Next Button -->
              <button 
                class="pagination-btn next-btn" 
                :disabled="currentPage === totalPages"
                @click="goToNextPage"
              >
                {{ locale === 'tr' ? 'Sonraki' : 'Next' }}
                <i class="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import ApiService from '../services/api.js'
import axios from 'axios'

const apiClient = axios.create({
  baseURL: 'https://api.aslanyazilimm.com',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
})

// Response interceptor
apiClient.interceptors.response.use(
  response => {
    // console.log('Paginated API Response:', response.config.url, response.status)
    return response.data || response
  },
  error => {
    // console.error('Paginated API Error:', error.message)
    throw new Error(error.message || 'API hatası')
  }
)

// Reactive data
const projects = ref([])
const isLoading = ref(true)
const error = ref(null)
const currentPage = ref(1)
const totalProjects = ref(0)
const projectsPerPage = 10
const locale = ref('tr')

// Computed properties
const totalPages = computed(() => Math.ceil(totalProjects.value / projectsPerPage))

const visiblePages = computed(() => {
  const pages = []
  const start = Math.max(1, currentPage.value - 2)
  const end = Math.min(totalPages.value, currentPage.value + 2)
  
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  return pages
})

// Methods
const loadProjects = async (page = 1) => {
  try {
    isLoading.value = true
    error.value = null
    
    // console.log(`🔄 Loading projects for page ${page}`)
    
    const response = await apiClient.get('/api/projects-paginated.php', {
      params: {
        page: page,
        limit: 10,
        offset: (page - 1) * 10
      }
    })
    
    // console.log('📦 Paginated projects response:', response)
    
    // API response structure: {success, message, data: {data: [], pagination: {}}}
    if (response && response.success && response.data && response.data.data && Array.isArray(response.data.data)) {
      // Pagination API response formatı
      projects.value = response.data.data.map(project => ({
        id: project.id,
        title: locale.value === 'tr' ? (project.title_tr || project.title) : (project.title_en || project.title),
        description: locale.value === 'tr' ? (project.description_tr || project.description) : (project.description_en || project.description),
        image: project.image_url || project.image,
        capacity: project.capacity,
        location: locale.value === 'tr' ? (project.location_tr || project.location) : (project.location_en || project.location),
        category: project.category,
        completion_date: project.completion_date,
        isFeatured: project.is_featured
      }))
      
      // Pagination bilgilerini güncelle
      if (response.data.pagination) {
        totalProjects.value = parseInt(response.data.pagination.total)
        currentPage.value = parseInt(response.data.pagination.page)
      } else {
        totalProjects.value = response.data.data.length
        currentPage.value = page
      }
    } else {
      // console.error('❌ Unexpected response format:', response)
      throw new Error('Geçersiz API yanıtı formatı')
    }
    
  } catch (err) {
    // console.error('❌ Error loading projects:', err)
    error.value = locale.value === 'tr' 
      ? 'Projeler yüklenirken bir hata oluştu.' 
      : 'An error occurred while loading projects.'
  } finally {
    isLoading.value = false
  }
}

const goToPage = (page) => {
  if (page !== currentPage.value) {
    loadProjects(page)
    scrollToTop()
  }
}

const goToPrevPage = () => {
  if (currentPage.value > 1) {
    goToPage(currentPage.value - 1)
  }
}

const goToNextPage = () => {
  if (currentPage.value < totalPages.value) {
    goToPage(currentPage.value + 1)
  }
}

const goToProjectDetail = (projectId) => {
  window.location.href = `?page=project&id=${projectId}`
}

const getProjectImage = (project) => {
  return ApiService.getImageUrl(
    project.image_url || project.image,
    'https://images.unsplash.com/photo-1466611653911-95081537e5b7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
  )
}

const handleImageError = (event) => {
  event.target.src = 'https://images.unsplash.com/photo-1466611653911-95081537e5b7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString(locale.value === 'tr' ? 'tr-TR' : 'en-US')
  } catch {
    return dateString
  }
}

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

// Lifecycle
onMounted(() => {
  loadProjects(1)
})

// Watch for URL changes (if needed)
watch(() => new URLSearchParams(window.location.search).get('page'), (newPage) => {
  if (newPage && !isNaN(newPage)) {
    loadProjects(parseInt(newPage))
  }
})
</script>

<style scoped>
.all-projects-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  position: relative;
  z-index: 0;
}

/* Header Styles */
.projects-header {
  background: linear-gradient(135deg, var(--primary-gold) 0%, #d4af37 100%);
  color: white;
  padding: 60px 0 80px;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.breadcrumb {
  margin-bottom: 20px;
  font-size: 14px;
}

.breadcrumb-link {
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: color 0.3s ease;
}

.breadcrumb-link:hover {
  color: white;
}

.breadcrumb-separator {
  margin: 0 10px;
  opacity: 0.6;
}

.breadcrumb-current {
  color: white;
  font-weight: 500;
}

.page-title {
  font-size: 3rem;
  font-weight: 700;
  margin: 0 0 20px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.page-description {
  font-size: 1.2rem;
  opacity: 0.9;
  max-width: 600px;
  line-height: 1.6;
}

/* Content Styles */
.projects-content {
  padding: 40px 0 80px;
  margin-top: -40px;
}

.projects-wrapper {
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 1;
}

.projects-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;
}

.results-count {
  font-size: 16px;
  color: #666;
}

.page-info {
  font-size: 14px;
  color: #888;
  background: #f8f9fa;
  padding: 8px 16px;
  border-radius: 20px;
}

/* Projects Grid */
.projects-grid {
  display: grid !important;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  margin-bottom: 50px;
  opacity: 1 !important;
  visibility: visible !important;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
}

.project-card {
  background: white !important;
  border-radius: 15px;
  overflow: visible;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  cursor: pointer;
  border: 2px solid var(--primary-gold);
  position: relative;
  z-index: 1;
  opacity: 1 !important;
  visibility: visible !important;
  display: block !important;
  width: 100%;
  max-width: 1100px;
}

.project-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
  border-color: var(--primary-gold);
}

.project-image-container {
  position: relative;
  height: 300px;
  overflow: hidden;
  opacity: 1 !important;
  visibility: visible !important;
}

.project-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.project-card:hover .project-image {
  transform: scale(1.1);
}

.project-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(201, 160, 64, 0.9) 0%, rgba(44, 62, 80, 0.8) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.project-card:hover .project-overlay {
  opacity: 1;
}

.view-details-btn {
  background: white;
  color: var(--primary-gold);
  border: none;
  padding: 12px 24px;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transform: translateY(20px);
  transition: transform 0.3s ease;
}

.project-card:hover .view-details-btn {
  transform: translateY(0);
}

.project-content {
  padding: 35px;
  position: relative;
  z-index: 2;
  background: white !important;
  opacity: 1 !important;
  visibility: visible !important;
  display: block !important;
  min-height: 150px;
}

.project-title {
  font-size: 1.4rem;
  font-weight: 600;
  margin: 0 0 12px;
  color: #2c3e50 !important;
  line-height: 1.3;
  position: relative;
  z-index: 3;
}

.project-description {
  color: #666 !important;
  line-height: 1.6;
  margin: 0 0 20px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  position: relative;
  z-index: 3;
}

.project-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #777;
}

.meta-item i {
  color: var(--primary-gold);
  width: 14px;
}

/* Pagination Styles */
.pagination-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 50px;
}

.pagination {
  display: flex;
  align-items: center;
  gap: 10px;
}

.pagination-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: 1px solid #ddd;
  background: white;
  color: #666;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
}

.pagination-btn:hover:not(:disabled) {
  background: var(--primary-gold);
  color: white;
  border-color: var(--primary-gold);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  gap: 5px;
  margin: 0 10px;
}

.page-number {
  width: 40px;
  height: 40px;
  border: 1px solid #ddd;
  background: white;
  color: #666;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
}

.page-number:hover {
  background: var(--primary-gold);
  color: white;
  border-color: var(--primary-gold);
}

.page-number.active {
  background: var(--primary-gold);
  color: white;
  border-color: var(--primary-gold);
}

/* Loading & Error States */
.loading-state, .error-state {
  text-align: center;
  padding: 80px 20px;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid var(--primary-gold);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-icon {
  font-size: 3rem;
  margin-bottom: 20px;
}

.retry-btn {
  background: var(--primary-gold);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  margin-top: 20px;
  transition: background 0.3s ease;
}

.retry-btn:hover {
  background: #d4af37;
}

/* Responsive Design */
@media (max-width: 768px) {
  .page-title {
    font-size: 2rem;
  }
  
  .projects-wrapper {
    padding: 20px;
  }
  
  .projects-info {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }
  
  .projects-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
  }
  
  .pagination {
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .pagination-btn {
    padding: 10px 16px;
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .projects-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .container {
    padding: 0 15px;
  }
  
  .page-numbers {
    display: none; /* Hide page numbers on very small screens */
  }
}

/* CSS Variables */
:root {
  --primary-gold: #c9a040;
}
</style>